import {Tokenizer} from 'htmlparser2';
import {identity} from 'lodash';

enum ProcessorContentTypesEnum {
  NON_TEXT = 0,
  ATTRIBUTE_VALUE = 1,
  NODE_TEXT_CONTENT = 2,
}

/**
 * Transforms text content and attribute values in potentially unsanitized HTML.
 *
 * @param dirtyHtml
 *   Unsanitized HTML.
 * @param processDirtyHtmlToken
 *   Called with raw/encoded HTML tokens for text content and attribute values. Should return the same.
 * @throws
 *   When an HTML tokenization error occurs.
 * @returns
 *   STILL UNSANITIZED HTML.
 */
export function processTextInDirtyHtml(
  dirtyHtml: string,
  processDirtyHtmlToken: (dirtyHtmlToken: string) => string,
): string {
  let newDirtyHtml = '';

  let bufferContentType = ProcessorContentTypesEnum.NON_TEXT;
  let bufferStartIndex = 0;
  let bufferEndIndex = 0;

  const flushBuffer = () => {
    if (bufferStartIndex < bufferEndIndex) {
      const processBufferContent =
        bufferContentType === ProcessorContentTypesEnum.NON_TEXT ? identity : processDirtyHtmlToken;
      newDirtyHtml += processBufferContent(dirtyHtml.slice(bufferStartIndex, bufferEndIndex));
      bufferStartIndex = bufferEndIndex;
    }
  };

  const consumeToken = (contentType: ProcessorContentTypesEnum, endIndex: number) => {
    // If this token is of a different content type, flush the currently buffered content.
    if (bufferContentType !== contentType) {
      flushBuffer();
    }

    // If this token is not-empty, start a new buffer.
    if (bufferEndIndex < endIndex) {
      bufferContentType = contentType;
      bufferEndIndex = endIndex;
    }
  };

  const tokenizer = new Tokenizer(
    {
      // Avoid touching things as much as possible.
      decodeEntities: false,
    },
    {
      onattribdata(start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, start);
        consumeToken(ProcessorContentTypesEnum.ATTRIBUTE_VALUE, endIndex);
      },
      onattribentity() {
        // This is called only if the tokenizer decodes entities.
        throw new Error('Unexpected onattribentity callback');
      },
      onattribend(_quote, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      onattribname(_start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      oncdata() {
        // This is called only if the tokenizer is in XML mode.
        throw new Error('Unexpected oncdata callback');
      },
      onclosetag(_start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      oncomment(_start, endIndex, _endOffset) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      ondeclaration(_start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      onend() {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, dirtyHtml.length);
        flushBuffer();
      },
      onopentagend(endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      onopentagname(_start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      onprocessinginstruction(_start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      onselfclosingtag(endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, endIndex);
      },
      ontext(start, endIndex) {
        consumeToken(ProcessorContentTypesEnum.NON_TEXT, start);
        consumeToken(ProcessorContentTypesEnum.NODE_TEXT_CONTENT, endIndex);
      },
      ontextentity() {
        // This is called only if the tokenizer decodes entities.
        throw new Error('Unexpected ontextentity callback');
      },
    },
  );
  tokenizer.write(dirtyHtml);
  tokenizer.end();
  return newDirtyHtml;
}
